import { motion } from "framer-motion";
import { Building2, Landmark, ShieldCheck, ArrowRight } from "lucide-react";

/**
 * MissionIllustration — a cross-border money-flow diagram rendered as a
 * product surface. Shows: host-country account → Zenstrust compliance layer →
 * India investment rails, with an animated capital flow.
 */

const RAILS = [
  { label: "Equity & mutual funds", meta: "NSE · BSE" },
  { label: "GIFT City IFSC funds", meta: "USD denominated" },
  { label: "Real estate", meta: "Tier-1 · Tier-2" },
];

export default function MissionIllustration({ className = "" }) {
  return (
    <div className={`relative w-full ${className}`} data-testid="mission-illustration">
      <div
        aria-hidden
        className="absolute -inset-6 rounded-[2rem] opacity-25 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle at 30% 30%, #1CB0A4 0%, transparent 65%)" }}
      />

      <div className="relative rounded-2xl bg-white border border-[hsl(var(--zt-beige-deep))] elev-3 p-6 lg:p-7 space-y-5">
        <div className="flex items-center justify-between">
          <p className="font-heading text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--zt-muted))] font-bold">
            How your capital moves
          </p>
          <span className="font-heading text-[10px] uppercase tracking-[0.1em] font-bold text-[hsl(var(--zt-sage))] bg-[hsl(var(--zt-sage))]/10 rounded-md px-2 py-1">
            Automated
          </span>
        </div>

        {/* Step 1 — source account */}
        <Node
          icon={Landmark}
          title="Your overseas account"
          meta="USD · GBP · AED · SGD"
          delay={0}
        />

        <Connector delay={0.25} />

        {/* Step 2 — compliance layer (accented) */}
        <motion.div
          className="rounded-xl bg-[hsl(var(--zt-teal))] p-4 relative overflow-hidden"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.35 }}
        >
          <div aria-hidden className="absolute inset-0 dot-grid-inverse opacity-40" />
          <div className="relative flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[hsl(var(--zt-gold))]/20 border border-[hsl(var(--zt-gold))]/35 grid place-items-center flex-shrink-0">
              <ShieldCheck size={16} className="text-[hsl(var(--zt-gold-soft))]" />
            </div>
            <div className="min-w-0">
              <p className="font-heading text-[13.5px] font-semibold text-white leading-tight">
                Zenstrust compliance layer
              </p>
              <p className="font-heading text-[11px] text-white/60 font-medium mt-1">
                KYC · FEMA · DTAA · Form 15CA/CB
              </p>
            </div>
          </div>
          <div className="relative mt-3.5 grid grid-cols-4 gap-1.5">
            {[0, 1, 2, 3].map((i) => (
              <motion.div
                key={i}
                className="h-1 rounded-full bg-[hsl(var(--zt-gold))]/70"
                initial={{ opacity: 0.25 }}
                animate={{ opacity: [0.25, 1, 0.25] }}
                transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.22, ease: "easeInOut" }}
              />
            ))}
          </div>
        </motion.div>

        <Connector delay={0.5} />

        {/* Step 3 — India rails */}
        <motion.div
          className="rounded-xl border border-[hsl(var(--zt-beige-deep))] bg-[hsl(var(--zt-beige))] p-4 space-y-2.5"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <div className="flex items-center gap-2 mb-1">
            <Building2 size={14} className="text-[hsl(var(--zt-gold))]" />
            <p className="font-heading text-[12px] uppercase tracking-[0.1em] font-bold text-[hsl(var(--zt-teal))]">
              India investment rails
            </p>
          </div>
          {RAILS.map((r, i) => (
            <motion.div
              key={r.label}
              className="flex items-center justify-between rounded-lg bg-white border border-[hsl(var(--zt-beige-deep))] px-3 py-2.5"
              initial={{ opacity: 0, x: -8 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.7 + i * 0.1 }}
            >
              <span className="font-heading text-[12.5px] font-medium text-[hsl(var(--zt-charcoal))]">{r.label}</span>
              <span className="font-heading text-[10.5px] text-[hsl(var(--zt-muted))] font-semibold">{r.meta}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

function Node({ icon: Icon, title, meta, delay }) {
  return (
    <motion.div
      className="flex items-center gap-3 rounded-xl border border-[hsl(var(--zt-beige-deep))] bg-white p-4"
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
    >
      <div className="w-9 h-9 rounded-lg bg-[hsl(var(--zt-teal))]/6 border border-[hsl(var(--zt-beige-deep))] grid place-items-center flex-shrink-0">
        <Icon size={16} className="text-[hsl(var(--zt-teal))]" />
      </div>
      <div className="min-w-0">
        <p className="font-heading text-[13.5px] font-semibold text-[hsl(var(--zt-teal))] leading-tight">{title}</p>
        <p className="font-heading text-[11px] text-[hsl(var(--zt-muted))] font-medium mt-1">{meta}</p>
      </div>
    </motion.div>
  );
}

function Connector({ delay }) {
  return (
    <motion.div
      className="flex items-center justify-center gap-2"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay }}
    >
      <span className="h-4 w-px bg-[hsl(var(--zt-beige-deep))]" />
      <ArrowRight size={13} className="text-[hsl(var(--zt-muted))] rotate-90" />
      <span className="h-4 w-px bg-[hsl(var(--zt-beige-deep))]" />
    </motion.div>
  );
}
